import React, { useState } from 'react';
import Typography from '@mui/material/Typography';
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    Slide,
    SxProps,
    Theme,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { ColorScheme, theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';
import { TransitionProps } from '@mui/material/transitions';
import { StyleSheet } from 'react-native';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { isEmpty } from '../../utils/lodash';
import { TMoreMenuItem } from './more.data';
import PlaylistIcon from '../CustomSVGIcons/PlaylistIcon';
import { IProject } from '../../types/global/helper';
import { useDispatch, useSelector } from 'react-redux';
import { userPlaylistSelector } from './selectors/index';
import { IPlaylistDetails, PlaylistEditor } from './PlaylistComponents/PlaylistEditor';
import { AddToPlaylistModal } from './PlaylistComponents/AddToPlaylistModal';
import { createPlaylist } from '../../redux/project/actions';
import CloseIcon from '../CustomSVGIcons/CloseIcon';

/**
 * Choosing to handle the playlist flow here to allow us simply import
 * This component anywhere and not deplicate the code
 */

interface IMore {
    onClick: (moreType: string) => void;
    menu: TMoreMenuItem[];
    sx?: SxProps<Theme>;
    iconStyle?: React.CSSProperties;
    onIconClick?: () => void;
    onClose?: () => void;
    title?: string;
    alternateMenuMessage?: string;
    alternateAction?: () => void;
    alternateActionButtonText?: string;
    alternateSubText?: string;
    disableButton?: boolean;
    customMoreIcon?: JSX.Element;
    customIconSx?: SxProps<Theme>;
    project?: IProject;
}

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement;
    },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const PlaylistMenu: React.FC<IMore> = ({
    onClick,
    menu,
    sx,
    iconStyle,
    onIconClick,
    onClose,
    title,
    alternateMenuMessage,
    alternateAction,
    alternateActionButtonText,
    alternateSubText,
    disableButton,
    customMoreIcon,
    customIconSx,
    project,
}) => {
    const { themeStyle, device } = useThemeContext();
    const themeui = useTheme();
    const fullScreen = useMediaQuery(themeui.breakpoints.down('md'));
    const styles = dynamicStyles(themeStyle);
    const [open, setOpen] = useState(false);
    const [openPlaylistEditor, setOpenPlaylistEditor] = useState(false);
    const [openPlaylistModal, setOpenPlaylistModal] = useState(false);

    const usersPlaylist = useSelector(userPlaylistSelector);
    const dispatch = useDispatch();

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setOpen(!open);
        onIconClick && onIconClick();
    };

    const handleClose = () => {
        setOpen(!open);
        onClose && onClose();
    };

    const handleOnClickHandle = (id: string) => {
        onClick && onClick(id);
        switch (id) {
            case 'new_playlist':
                setOpenPlaylistEditor(!openPlaylistEditor);
                break;
            case 'edit_playlist':
                setOpenPlaylistModal(!openPlaylistModal);
                break;
        }
        handleClose();
    };

    const handleOnPlaylistEditorSubmit = (details: IPlaylistDetails) => {
        if (project) {
            dispatch(
                createPlaylist({
                    name: details.name,
                    description: details.description,
                    playlist_type: project.media_type!,
                    projects: [project._id as unknown as IProject],
                }),
            );
        }
        setOpenPlaylistEditor(!openPlaylistEditor);
    };

    const renderCreateNewPlaylist = (
        <PlaylistEditor
            onSubmit={handleOnPlaylistEditorSubmit}
            open={openPlaylistEditor}
            onClose={() => setOpenPlaylistEditor(!openPlaylistEditor)}
        />
    );

    const renderAddToExistingPlaylist = (
        <AddToPlaylistModal
            open={openPlaylistModal}
            onClose={() => setOpenPlaylistModal(!openPlaylistModal)}
            userPlaylists={usersPlaylist}
            selectedProject={project}
        />
    );

    const renderMenu = (
        <Dialog
            scroll="paper"
            open={open}
            onClose={handleClose}
            TransitionComponent={Transition}
            fullWidth={false}
            fullScreen={false}
            maxWidth={'lg'}
            PaperProps={{
                style: {
                    backgroundColor: fullScreen
                        ? theme[themeStyle].background_gradient.mid
                        : theme[themeStyle].background_gradient.start, // make this a color variable
                    width: '90vw',
                    maxWidth: device.isMobile ? '100%' : '500px',
                    borderRadius: 20,
                    border: `0.5px solid ${theme[themeStyle].darkGray60}`,
                    backdropFilter: 'blur(0.5px)',
                    margin: 0,
                },
            }}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                outline: 'none',
                backgroundColor: theme[themeStyle].modal_dark,
            }}>
            <DialogTitle>
                <Box
                    sx={{
                        width: '100%',
                        height: 'fit-content',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}>
                    <Typography style={styles.title}>{title}</Typography>
                    <CloseIcon onClick={handleClose} style={styles.close} stroke={theme[themeStyle].tag_white} />
                </Box>
            </DialogTitle>
            <DialogContent>
                <Box
                    style={{
                        padding: '8px',
                        minWidth: '150px',
                    }}>
                    {isEmpty(menu) && <Typography style={styles.button}>{alternateMenuMessage}</Typography>}
                    {alternateSubText && alternateAction && alternateActionButtonText && isEmpty(menu) && (
                        <Typography style={styles.subText}>{alternateSubText}</Typography>
                    )}
                    {alternateAction && alternateActionButtonText && isEmpty(menu) && (
                        <Button
                            onClick={() => {
                                alternateAction && alternateAction();
                                setOpen(!open);
                            }}
                            disabled={disableButton}
                            style={{
                                height: '52px',
                                borderRadius: 100,
                                fontSize: 16,
                                fontWeight: '600',
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                            sx={{
                                textDecoration: 'none',
                                backgroundColor: theme[themeStyle].deep_grey_lighter,
                                border: `2px solid ${theme[themeStyle].primary_orange}`,
                                color: theme[themeStyle].pure_black,
                                textTransform: 'none',
                                width: '100%',
                                '&:hover': {
                                    border: `2px solid ${theme[themeStyle].primary_orange}`,
                                    backgroundColor: theme[themeStyle].tertiary_white,
                                    color: theme[themeStyle].primary_orange,
                                },
                                my: 3,
                            }}>
                            <Typography style={styles.button}>{alternateActionButtonText}</Typography>
                        </Button>
                    )}
                    {!isEmpty(menu) &&
                        menu.map(
                            (d, i) =>
                                !!d &&
                                d.id &&
                                d.title && (
                                    <Button
                                        key={i}
                                        onClick={() => handleOnClickHandle(d.id)}
                                        style={{
                                            height: '52px',
                                            borderRadius: 100,
                                            fontSize: 16,
                                            fontWeight: '600',
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                        sx={{
                                            textDecoration: 'none',
                                            backgroundColor: theme[themeStyle].deep_grey_lighter,
                                            border: `2px solid ${theme[themeStyle].primary_orange}`,
                                            color: theme[themeStyle].pure_black,
                                            textTransform: 'none',
                                            width: '100%',
                                            '&:hover': {
                                                border: `2px solid ${theme[themeStyle].primary_orange}`,
                                                backgroundColor: theme[themeStyle].tertiary_white,
                                                color: theme[themeStyle].primary_orange,
                                            },
                                            my: 3,
                                        }}>
                                        <Typography style={styles.button}>{d.title}</Typography>
                                    </Button>
                                ),
                        )}
                </Box>
            </DialogContent>
        </Dialog>
    );

    return (
        <>
            <Box onClick={handleClick} sx={customIconSx}>
                {customMoreIcon ?? (
                    <PlaylistIcon
                        fill={theme[themeStyle].true_white}
                        style={{
                            backgroundColor: theme[themeStyle].true_black,
                            borderRadius: '3px',
                            padding: '4px',
                            ...iconStyle,
                        }}
                    />
                )}
            </Box>
            {renderMenu}
            {renderCreateNewPlaylist}
            {renderAddToExistingPlaylist}
        </>
    );
};

export { PlaylistMenu };

const dynamicStyles = (colorScheme: ColorScheme) =>
    StyleSheet.create({
        close: {
            width: 'fit-content',
            height: 'fit-content',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            position: 'absolute',
            right: '10px',
            top: '10px',
            cursor: 'pointer',
        },
        title: {
            textAlign: 'left',
            fontSize: 24,
            fontStyle: 'normal',
            fontWeight: '600',
            color: theme[colorScheme].primary_white,
            fontFamily: FONT_ENUM.MAIN_BOLD,
            textTransform: 'capitalize',
        },
        button: {
            fontFamily: FONT_ENUM.MEDIUM_HEADER,
            color: theme[colorScheme].primary_white,
        },
        subText: {
            fontFamily: FONT_ENUM.REGULAR_HEADER,
            color: theme[colorScheme].light_gray_text,
            fontSize: 12,
            marginTop: 10,
        },
    });
